import { useEffect, useState, useCallback, useRef } from "react";
import { Search, X, ChevronLeft, ChevronRight, ChevronDown, Loader2, Briefcase, ListChecks, RefreshCw, Trash2 } from "lucide-react";
import {
  fetchUpworkJobs, fetchAssignments, fetchAssignmentStatusCounts, assignJob, unassignJob,
  reassignAssignment, updateAssignmentStatus, syncUpworkJobsNow,
  fetchOldJobsCleanupPreview, executeOldJobsCleanup,
  STATUS_OPTIONS, TERMINAL,
} from "./api/UpworkJobsApi";
import type {
  UpworkJob, UpworkAssignment, PagedResult, JobFilters, AssignmentFilters, AssignmentStatus, SyncResult,
  OldJobsCleanupPreview,
} from "./api/UpworkJobsApi";
import { fetchTechManagers } from "features/theirstack-jobs/api/TheirStackJobsApi";
import type { TheirStackTechManager } from "features/theirstack-jobs/api/TheirStackJobsApi";
import JobCard from "./ui/JobCard";
import AssignmentCard from "./ui/AssignmentCard";
import JobDetailModal from "./ui/JobDetailModal";
import AssignmentDetailModal from "./ui/AssignmentDetailModal";
import CleanupConfirmModal from "./ui/CleanupConfirmModal";
import AppSelect from "shared/AppSelect";
import ViewToggle from "shared/ViewToggle";
import StatusKanbanBoard, { type KanbanColumn } from "shared/StatusKanbanBoard";

const STATUS_CHIP: Record<string, { label: string; className: string }> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, { label: o.label, className: TERMINAL.has(o.value) ? "bg-muted text-muted-foreground border-border" : "bg-primary text-primary-foreground border-primary/70" }])
);

const ASSIGNMENTS_VIEW_KEY = "hrms_upwork_jobs_view";
const BOARD_COLUMNS: KanbanColumn[] = STATUS_OPTIONS.map((o) => ({
  key: o.value,
  label: o.label,
  isTerminal: TERMINAL.has(o.value),
}));

interface Props {
  isAdmin: boolean;
}

export default function UpworkJobsPage({ isAdmin }: Props) {
  const [browsingJobs, setBrowsingJobs] = useState(false);
  const isAssignmentsMode = isAdmin ? !browsingJobs : true;

  const [jobs, setJobs] = useState<PagedResult<UpworkJob> | null>(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobFilters, setJobFiltersState] = useState<JobFilters>({ page: 1, page_size: 20 });
  const setJobFilters = (patch: Partial<JobFilters>) => setJobFiltersState((f) => ({ ...f, ...patch }));

  const [assignments, setAssignments] = useState<PagedResult<UpworkAssignment> | null>(null);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentFilters, setAssignmentFiltersState] = useState<AssignmentFilters>({ page: 1, page_size: 20, hide_archived: true });
  const setAssignmentFilters = (patch: Partial<AssignmentFilters>) => setAssignmentFiltersState((f) => ({ ...f, ...patch }));

  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [techManagers, setTechManagers] = useState<TheirStackTechManager[]>([]);

  const [selectedJob, setSelectedJob] = useState<UpworkJob | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<UpworkAssignment | null>(null);

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  // Old-jobs cleanup — jobs are never auto-deleted; this is an explicit,
  // admin-triggered, preview-then-confirm action (mirrors LinkedIn Jobs' own
  // "Delete Old Jobs" cleanup).
  const [cleanupDropdownOpen, setCleanupDropdownOpen] = useState(false);
  const cleanupDropdownRef = useRef<HTMLDivElement>(null);
  const [cleanupModal, setCleanupModal] = useState<{ months: number; preview: OldJobsCleanupPreview } | null>(null);
  const [cleanupExecuting, setCleanupExecuting] = useState(false);
  const [cleanupError, setCleanupError] = useState<string | null>(null);

  const [assignmentsView, setAssignmentsView] = useState<"list" | "board">(() =>
    localStorage.getItem(ASSIGNMENTS_VIEW_KEY) === "list" ? "list" : "board"
  );
  const switchAssignmentsView = (v: "list" | "board") => {
    setAssignmentsView(v);
    localStorage.setItem(ASSIGNMENTS_VIEW_KEY, v);
  };
  // Board shows every status as its own column, each paginated independently
  // (hide_archived only makes sense for the flat list) — keeps every other
  // filter in sync with the list.
  const fetchBoardColumn = useCallback(async (statusKey: string, page: number, pageSize: number) => {
    const result = await fetchAssignments(
      {
        page,
        page_size: pageSize,
        status_filter: statusKey,
        search: assignmentFilters.search,
        search_profile: assignmentFilters.search_profile,
        experience_level: assignmentFilters.experience_level,
        hide_archived: false,
        employee_id: assignmentFilters.employee_id,
      },
      isAdmin,
    );
    return { items: result.items, total: result.total_count };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentFilters, isAdmin]);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      setJobs(await fetchUpworkJobs(jobFilters));
    } catch {
      setJobs(null);
    } finally {
      setJobsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobFilters]);

  const loadAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      setAssignments(await fetchAssignments(assignmentFilters, isAdmin));
    } catch {
      setAssignments(null);
    } finally {
      setAssignmentsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentFilters, isAdmin]);

  const loadStatusCounts = useCallback(async () => {
    try {
      setStatusCounts(await fetchAssignmentStatusCounts(isAdmin, isAdmin ? assignmentFilters.employee_id : undefined));
    } catch {
      setStatusCounts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, assignmentFilters.employee_id]);

  useEffect(() => {
    if (isAdmin) fetchTechManagers().then(setTechManagers).catch(() => setTechManagers([]));
  }, [isAdmin]);

  useEffect(() => {
    if (isAssignmentsMode) {
      loadAssignments();
      loadStatusCounts();
    } else {
      loadJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isAssignmentsMode]);

  useEffect(() => {
    if (isAdmin && !isAssignmentsMode) loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobFilters]);

  useEffect(() => {
    if (!isAdmin || isAssignmentsMode) loadAssignments();
    if (isAdmin && isAssignmentsMode) loadStatusCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentFilters]);

  const [localJobSearch, setLocalJobSearch] = useState("");
  const [localAssignSearch, setLocalAssignSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setJobFilters({ search: localJobSearch, page: 1 }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localJobSearch]);

  useEffect(() => {
    const t = setTimeout(() => setAssignmentFilters({ search: localAssignSearch, page: 1 }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAssignSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (cleanupDropdownRef.current && !cleanupDropdownRef.current.contains(e.target as Node)) {
        setCleanupDropdownOpen(false);
      }
    }
    if (cleanupDropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [cleanupDropdownOpen]);

  async function handleSyncNow() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncUpworkJobsNow();
      setSyncResult(result);
      if (isAssignmentsMode) { loadAssignments(); loadStatusCounts(); } else { loadJobs(); }
    } catch (err: any) {
      setSyncResult({ profiles_run: 0, inserted: 0, updated: 0, skipped: 0, auto_assigned: 0, error: err?.response?.data?.detail ?? "Sync failed" });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 8000);
    }
  }

  async function openOldJobsCleanup(months: number) {
    setCleanupDropdownOpen(false);
    setCleanupError(null);
    try {
      const preview = await fetchOldJobsCleanupPreview(months);
      setCleanupModal({ months, preview });
    } catch {
      setCleanupError("Failed to load cleanup preview. Please try again.");
    }
  }

  async function executeCleanup() {
    if (!cleanupModal) return;
    setCleanupExecuting(true);
    setCleanupError(null);
    try {
      await executeOldJobsCleanup(cleanupModal.months, cleanupModal.preview.cutoff_iso);
      setCleanupModal(null);
      if (isAssignmentsMode) { loadAssignments(); loadStatusCounts(); } else { loadJobs(); }
    } catch {
      setCleanupError("Cleanup failed. Please try again.");
    } finally {
      setCleanupExecuting(false);
    }
  }

  async function handleAssign(jobId: number, employeeIds: number[]) {
    return assignJob(jobId, employeeIds);
  }
  async function handleUnassign(jobId: number, employeeId: number) {
    await unassignJob(jobId, employeeId);
  }
  async function handleReassign(assignmentId: number, employeeId: number) {
    await reassignAssignment(assignmentId, employeeId);
    loadAssignments();
  }
  async function handleStatusUpdate(assignmentId: number, status: AssignmentStatus, notes: string | null) {
    await updateAssignmentStatus(assignmentId, status, notes, isAdmin);
    loadAssignments();
    loadStatusCounts();
    setSelectedAssignment((prev) => (prev && prev.assignment_id === assignmentId ? { ...prev, status, assignment_notes: notes } : prev));
  }

  const totalStatusCount = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const trackedCount = isAssignmentsMode ? (assignments?.total_count ?? totalStatusCount) : (jobs?.total_count ?? 0);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-inter">Upwork</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">
            Jobs sourced from the company's connected Upwork account. Employees apply manually from their own Upwork account — HRMS never applies on anyone's behalf.
          </p>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">{trackedCount} job{trackedCount !== 1 ? "s" : ""} tracked</p>
        </div>

        <div className="flex items-center gap-2">
          {isAdmin && isAssignmentsMode && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground font-inter whitespace-nowrap">Employee:</label>
              <AppSelect
                value={assignmentFilters.employee_id?.toString() ?? ""}
                onChange={(v) => setAssignmentFilters({ employee_id: v ? parseInt(v, 10) : undefined, page: 1 })}
                className="w-48"
              >
                <option value="">All TMs</option>
                {techManagers.map((tm) => <option key={tm.id} value={tm.id.toString()}>{tm.name}</option>)}
              </AppSelect>
            </div>
          )}

          {isAdmin && (
            <div className="flex items-center gap-1 bg-surface border border-border rounded-lg p-0.5">
              <button
                onClick={() => setBrowsingJobs(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-inter font-medium transition-colors ${!isAssignmentsMode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Briefcase className="w-3.5 h-3.5" />
                Browse Jobs
              </button>
              <button
                onClick={() => setBrowsingJobs(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-inter font-medium transition-colors ${isAssignmentsMode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                <ListChecks className="w-3.5 h-3.5" />
                Assignments
              </button>
            </div>
          )}

          {isAssignmentsMode && (
            <ViewToggle view={assignmentsView} onChange={switchAssignmentsView} />
          )}

          {isAdmin && (
            <button
              onClick={handleSyncNow}
              disabled={syncing}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-muted-foreground rounded-xl transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              Sync Now
            </button>
          )}

          {/* Delete Old Jobs — admin only. Jobs are never auto-deleted; this is an
              explicit, preview-then-confirm cleanup action. */}
          {isAdmin && (
            <div className="relative" ref={cleanupDropdownRef}>
              <button
                onClick={() => setCleanupDropdownOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive border border-border hover:border-destructive/50 rounded-xl transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Old Jobs
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${cleanupDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {cleanupDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-surface-muted border border-border rounded-xl shadow-xl z-20 overflow-hidden">
                  <button
                    onClick={() => openOldJobsCleanup(1)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-surface hover:text-foreground font-inter transition-colors"
                  >
                    Delete 1 Month+ Old Jobs
                  </button>
                  <button
                    onClick={() => openOldJobsCleanup(6)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-surface hover:text-foreground font-inter transition-colors"
                  >
                    Delete 6 Month+ Old Jobs
                  </button>
                  <button
                    onClick={() => openOldJobsCleanup(12)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-surface hover:text-foreground font-inter transition-colors"
                  >
                    Delete 1 Year+ Old Jobs
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {cleanupError && !cleanupModal && (
        <div className="text-sm font-inter rounded-xl px-4 py-3 border text-red-400 bg-red-500/10 border-red-500/30">
          {cleanupError}
        </div>
      )}

      {syncResult && (
        <div
          className={`text-sm font-inter rounded-xl px-4 py-3 border ${
            syncResult.error
              ? "text-red-400 bg-red-500/10 border-red-500/30"
              : syncResult.timed_out
              ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
              : "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
          }`}
        >
          {syncResult.error
            ? syncResult.error
            : syncResult.timed_out
            ? `Sync stopped early after ${syncResult.profiles_run} profile(s) to stay within the time limit: ${syncResult.inserted} new, ${syncResult.updated} updated, ${syncResult.auto_assigned} auto-assigned. Run Sync Now again to continue.`
            : `Synced ${syncResult.profiles_run} profile(s): ${syncResult.inserted} new, ${syncResult.updated} updated, ${syncResult.auto_assigned} auto-assigned.`}
        </div>
      )}

      {/* ── Browse Jobs (admin) ── */}
      {!isAssignmentsMode && isAdmin && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={localJobSearch}
                onChange={(e) => setLocalJobSearch(e.target.value)}
                placeholder="Search jobs…"
                className="w-full bg-surface border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary/60 transition-colors"
              />
              {localJobSearch && (
                <button onClick={() => setLocalJobSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!jobFilters.active_only}
                onChange={(e) => setJobFilters({ active_only: e.target.checked, page: 1 })}
                className="rounded border-border text-primary focus:ring-primary"
              />
              Active only
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!jobFilters.unassigned_only}
                onChange={(e) => setJobFilters({ unassigned_only: e.target.checked, page: 1 })}
                className="rounded border-border text-primary focus:ring-primary"
              />
              Unassigned only
            </label>
          </div>

          {jobsLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : !jobs || jobs.items.length === 0 ? (
            <div className="text-center py-16"><p className="text-muted-foreground font-inter text-sm">No jobs found.</p></div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {jobs.items.map((job) => <JobCard key={job.id} job={job} onClick={() => setSelectedJob(job)} />)}
              </div>
              <Pagination page={jobs.page} totalPages={jobs.total_pages} totalCount={jobs.total_count} onChange={(p) => setJobFilters({ page: p })} />
            </>
          )}
        </>
      )}

      {/* ── Assignments ── */}
      {isAssignmentsMode && (
        <>
          {/* Status chips — a status filter doesn't make sense in board view,
              since the board already shows every status as its own column */}
          {assignmentsView === "list" && (
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setAssignmentFilters({ status_filter: "", hide_archived: true, page: 1 })}
                className={`text-xs px-2.5 py-1 rounded-lg border font-inter transition-colors ${!assignmentFilters.status_filter ? "bg-muted text-foreground border-border" : "text-muted-foreground border-border hover:border-muted-foreground hover:text-foreground"}`}
              >
                All ({totalStatusCount})
              </button>
              {STATUS_OPTIONS.map((opt) => {
                const chip = STATUS_CHIP[opt.value];
                const isSelected = assignmentFilters.status_filter === opt.value;
                const count = statusCounts[opt.value] ?? 0;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setAssignmentFilters({ status_filter: opt.value, hide_archived: false, page: 1 })}
                    className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter transition-colors ${isSelected ? chip.className : "text-muted-foreground border-border hover:border-muted-foreground hover:text-foreground"}`}
                  >
                    {chip.label}{count > 0 ? ` (${count})` : ""}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={localAssignSearch}
                onChange={(e) => setLocalAssignSearch(e.target.value)}
                placeholder="Search assignments…"
                className="w-full bg-surface border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary/60 transition-colors"
              />
              {localAssignSearch && (
                <button onClick={() => setLocalAssignSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {assignmentsView === "list" && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!assignmentFilters.hide_archived}
                  onChange={(e) => setAssignmentFilters({ hide_archived: e.target.checked, status_filter: "", page: 1 })}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                Hide terminal
              </label>
            )}
          </div>

          {/* Board view */}
          {assignmentsView === "board" && (
            <StatusKanbanBoard
              columns={BOARD_COLUMNS}
              getId={(a) => a.assignment_id}
              getStatus={(a) => a.status}
              withStatus={(a, status) => ({ ...a, status: status as AssignmentStatus })}
              fetchColumnItems={fetchBoardColumn}
              onStatusChange={(a, status) => handleStatusUpdate(a.assignment_id, status as AssignmentStatus, null)}
              renderCard={(a) => {
                const tmName = isAdmin ? techManagers.find((tm) => tm.id === a.employee_id)?.name : undefined;
                return (
                  <AssignmentCard
                    assignment={a}
                    showEmployee={isAdmin}
                    employeeName={tmName}
                    techManagers={isAdmin ? techManagers : undefined}
                    onReassign={isAdmin ? handleReassign : undefined}
                    onStatusChange={handleStatusUpdate}
                    onClick={() => setSelectedAssignment(a)}
                    compact
                  />
                );
              }}
            />
          )}

          {assignmentsView === "list" && (assignmentsLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : !assignments || assignments.items.length === 0 ? (
            <div className="text-center py-16"><p className="text-muted-foreground font-inter text-sm">No assignments found.</p></div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assignments.items.map((a) => {
                  const tmName = isAdmin ? techManagers.find((tm) => tm.id === a.employee_id)?.name : undefined;
                  return (
                    <AssignmentCard
                      key={a.assignment_id}
                      assignment={a}
                      showEmployee={isAdmin}
                      employeeName={tmName}
                      techManagers={isAdmin ? techManagers : undefined}
                      onReassign={isAdmin ? handleReassign : undefined}
                      onStatusChange={handleStatusUpdate}
                      onClick={() => setSelectedAssignment(a)}
                    />
                  );
                })}
              </div>
              <Pagination page={assignments.page} totalPages={assignments.total_pages} totalCount={assignments.total_count} onChange={(p) => setAssignmentFilters({ page: p })} />
            </>
          ))}
        </>
      )}

      {/* Modals */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          techManagers={techManagers}
          onClose={() => setSelectedJob(null)}
          onAssign={handleAssign}
          onUnassign={handleUnassign}
        />
      )}
      {selectedAssignment && (
        <AssignmentDetailModal
          assignment={selectedAssignment}
          isAdmin={isAdmin}
          techManagers={techManagers}
          onClose={() => setSelectedAssignment(null)}
          onStatusUpdate={handleStatusUpdate}
          onReassign={isAdmin ? handleReassign : undefined}
        />
      )}
      {cleanupModal && (
        <CleanupConfirmModal
          months={cleanupModal.months}
          preview={cleanupModal.preview}
          executing={cleanupExecuting}
          error={cleanupError}
          onConfirm={executeCleanup}
          onClose={() => { setCleanupModal(null); setCleanupError(null); }}
        />
      )}
    </div>
  );
}

function Pagination({ page, totalPages, totalCount, onChange }: { page: number; totalPages: number; totalCount: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between pt-2">
      <p className="text-xs text-muted-foreground font-inter">{totalCount} total</p>
      <div className="flex items-center gap-2">
        <button onClick={() => onChange(page - 1)} disabled={page <= 1} className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-muted-foreground font-inter">{page} / {totalPages}</span>
        <button onClick={() => onChange(page + 1)} disabled={page >= totalPages} className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
